import React, { useEffect, useState } from 'react';
import Service from './Service';
import './MyOrder.scss';
import { jwtDecode } from 'jwt-decode';
import StatusTimeline from '../../../Components/StatusTimeline/StatusTimeline';
import { useNavigate, useLocation } from 'react-router-dom';

const MyOrder = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // 🟢 1. Read userId from navigation state (Dashboard → MyOrder)
  const passedUserId = location.state?.userId || null;

  // 🟢 2. Read orderId (this is the new part)
  const passedOrderId = location.state?.orderId || null;

  // 🟢 3. Determine actual userId
  let userId = passedUserId;
  if (!userId) {
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        userId = decoded.user_id;
      } catch (err) {
        console.error('Invalid token:', err);
      }
    }
  }

  // 🟢 Fetch orders for the user
  useEffect(() => {
    const fetchOrders = async () => {
      if (!userId) {
        setError('User not logged in.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await Service.getOrderList(userId);

        const formattedOrders = response.data.map((order) => {
          const isRetest = !!order['retest-order'];
          return {
            id: order.id,
            order_number: order.order_number,
            created_at: order.created_at,
            financial_status: order.financial_status,
            fulfillment_status: order.fulfillment_status || 'Pending',
            status_detail: order?.db_order?.status_detail || { code: 'pending', label: 'Pending' },
            total_price: order.total_price,
            currency: order.currency,
            email: order.email,
            billing_address: order.billing_address,
            shipping_address: order.shipping_address,
            isRetest,
            products: (order.line_items || []).map((item) => ({
              id: item.id,
              product_name: item.name,
              price: item.price,
              quantity: item.quantity,
              total_discount: item.total_discount,
            })),
            shipping_lines: (order.shipping_lines || []).map((line) => ({
              title: line.title,
              price: line.price || line.discounted_price,
            })),
            total_tax: order.total_tax,
            total_discounts: order.total_discounts,
            tags: order.tags,
            payment_gateway_names: (order.payment_gateway_names || []).join(', '),
            order_status: order?.db_order?.status_detail,
            db_order: order.db_order,
          };
        });

        setOrders(formattedOrders);
      } catch (err) {
        setError('Failed to fetch orders.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userId]);

  // 🟢 After orders load → auto select the order passed from Dashboard
  useEffect(() => {
    if (!loading && orders.length > 0 && passedOrderId) {
      const autoSelect = orders.find((o) => o.id === passedOrderId);
      if (autoSelect) {
        setSelectedOrder(autoSelect);
      }
    }
  }, [loading, orders, passedOrderId]);

  // Formatting helpers
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.name || ''}\n${address.address1 || ''}${
      address.address2 ? `, ${address.address2}` : ''
    }\n${address.city || ''}, ${address.province_code || ''} ${address.zip || ''}\n${address.country || ''}`;
  };

  const handleDownloadReport = (order) => {
    const pdfUrl = order?.db_order?.pdf_documents?.[0]?.presigned_url;
    if (!pdfUrl) {
      alert('No report available for this order.');
      return;
    }

    const link = document.createElement('a');
    link.href = pdfUrl;
    const fileName = order?.db_order?.pdf_documents?.[0]?.title || `order_${order.order_number}_report.pdf`;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewReport = (orderId) => {
    navigate('/report', { state: { orderId } });
  };

  const tassoStatusFlow = [
    { code: 'accepted', label: 'Order Accepted' },
    { code: 'pendingfulfillment', label: 'At Tasso' },
    { code: 'inlogistics', label: 'In logistics' },
    { code: 'readytoship', label: 'Ready to Ship' },
    { code: 'atpatient', label: 'At Patient' },
    { code: 'intransittolab', label: 'In Transit To Lab' },
    { code: 'atlab', label: 'At Lab' },
    { code: 'resultsready', label: 'Results Ready' },
    { code: 'replaced', label: 'Replaced' },
  ];

  const getTassoStatusLabel = (tassoStatus) => {
    if (!tassoStatus) return null;

    const statusCode = typeof tassoStatus === 'string' ? tassoStatus.toLowerCase().trim() : tassoStatus.code?.toLowerCase().trim();

    const mappedStatus = tassoStatusFlow.find((status) => status.code.toLowerCase() === statusCode);

    return mappedStatus?.label || tassoStatus.label || statusCode;
  };

  const getCurrentStatusLabel = (order) => {
    const mailmanStatus = order?.db_order?.mailman_status_detail;
    const tassoStatus = order?.db_order?.tasso_status_detail;
    const tassoLabel = getTassoStatusLabel(tassoStatus);

    if (mailmanStatus?.label && tassoLabel) {
      return (
        <>
          <div>Semen Analysis Kit: {mailmanStatus.label}</div>
          <div>Hormone Panel Kit: {tassoLabel}</div>
        </>
      );
    }
    if (mailmanStatus?.label) return mailmanStatus.label;
    if (tassoLabel) return tassoLabel;
    return 'Waiting for clinician to assign';
  };

  if (loading) return <div className="loader">Loading orders...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="myOrder-page">
      <div className="container">
        {/* 🟢 If selectedOrder exists, directly show full order details */}
        {selectedOrder ? (
          <div key={selectedOrder.id} className="order-card full-view">
            <div className="order-actions">
              <button className="btn back-button" onClick={() => setSelectedOrder(null)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Orders
              </button>
              <div className="report-actions">
                <button className="btn btn-primary" onClick={() => handleDownloadReport(selectedOrder)}>
                  Download Report
                </button>
                <button className="btn back-button" onClick={() => handleViewReport(selectedOrder.id)}>
                  View Report
                </button>
              </div>
            </div>

            <div className="order-header">
              <h2>Order #{selectedOrder.order_number}</h2>
              <span className={`status ${selectedOrder.financial_status?.toLowerCase()}`}>{selectedOrder.financial_status}</span>
            </div>

            <StatusTimeline
              currentStatus={{
                mailman: selectedOrder?.db_order?.mailman_status_detail,
                tasso: selectedOrder?.db_order?.tasso_status_detail,
              }}
              type={
                selectedOrder?.db_order?.tasso_status_detail && selectedOrder?.db_order?.mailman_status_detail
                  ? ['mailman', 'tasso']
                  : selectedOrder?.db_order?.type
              }
            />

            <div className="order-info">
              <p>
                <strong>Order Date:</strong> {formatDate(selectedOrder.created_at)}
              </p>
              <p>
                <strong>Fulfillment:</strong> {selectedOrder.fulfillment_status}
              </p>
              <p>
                <strong>Email:</strong> <span className="black-text">{selectedOrder.email}</span>
              </p>
              {/* <p>
              <strong>Tags:</strong> {selectedOrder.tags || 'None'}
            </p> */}
            </div>

            {!selectedOrder.isRetest && (
              <div className="addresses">
                <div>
                  <h4>Billing Address</h4>
                  <pre>{formatAddress(selectedOrder.billing_address)}</pre>
                </div>
                <div>
                  <h4>Shipping Address</h4>
                  <pre>{formatAddress(selectedOrder.shipping_address)}</pre>
                </div>
              </div>
            )}

            <div className="products">
              <h3>Products</h3>
              {selectedOrder.products.map((product) => (
                <div key={product.id} className="product-item">
                  <div className="product-name">{product.product_name}</div>
                  <div className="product-qty">Qty: {product.quantity}</div>
                  <div className="product-price">
                    {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {product.price}
                  </div>
                  <div className="product-discount">
                    Discount: {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {product.total_discount}
                  </div>
                </div>
              ))}
            </div>

            <div className="shipping">
              <h3>Shipping</h3>
              {selectedOrder.shipping_lines.map((line, i) => (
                <div key={i} className="shipping-item">
                  <div>{line.title}</div>
                  <div>
                    {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {line.price}
                  </div>
                </div>
              ))}
            </div>

            <div className="totals">
              <h3>Payment & Totals</h3>
              <p>
                <strong>Payment:</strong> {selectedOrder.payment_gateway_names || 'N/A'}
              </p>
              <p>
                <strong>Total Discounts:</strong> {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {selectedOrder.total_discounts}
              </p>
              <p>
                <strong>Total Tax:</strong> {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {selectedOrder.total_tax}
              </p>
              <div className="order-total">
                <strong>
                  Total: {selectedOrder.currency === 'USD' ? '$' : selectedOrder.currency} {selectedOrder.total_price}
                </strong>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* ORDER LIST TABLE */}
            <h3 className="section-title mb-5">My orders</h3>
            {orders.length === 0 ? (
              <div className="no-orders">No orders found.</div>
            ) : (
              <div className="table-wrapper">
                <table className="order-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Order Date</th>
                      <th>Email</th>
                      <th>Total</th>
                      <th>Payment Status</th>
                      <th>Order Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.order_number}</td>
                        <td>{formatDate(order.created_at)}</td>
                        <td>{order.email}</td>
                        <td>
                          {order.currency === 'USD' ? '$' : order.currency} {order.total_price}
                        </td>
                        <td>{order.financial_status?.charAt(0).toUpperCase() + order.financial_status?.slice(1).toLowerCase()}</td>
                        <td>{getCurrentStatusLabel(order)}</td>
                        <td>
                          <button className="btn btn-primary view-details-btn" onClick={() => setSelectedOrder(order)}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyOrder;
